import { useMemo } from "react";

import { useSelector } from "react-redux";

import crmEnrichmentFiltersService from "app/pages/sales-intelligence/modules/crm-enrichment/services/filters";
import type {
    CrmEnrichmentJob,
    CrmEnrichmentJobFilterType,
    CrmEnrichmentJobObject,
    CrmEnrichmentJobRefreshPeriod,
    EnrichmentJobMappingDTO,
    LeadScoreRefreshDataInterval,
} from "app/pages/sales-intelligence/modules/crm-enrichment/types/types";
import {
    areMappingsUnchanged,
    areMappingsValid,
} from "app/pages/sales-intelligence/modules/crm-enrichment/utils/mappingValidation";
import {
    selectHsAccountsColumnsRaw,
    selectHsContactsColumnsRaw,
} from "app/pages/sales-intelligence/sub-modules/management/store/selectors/hubspot/exportSelectors";
import type { ICountryObject } from "app/services/CountryService";

const useCrmEnrichmentJobEditorValidation = ({
    jobName,
    isApplyFiltersSelected,
    filters,
    selectedJobObject,
    selectedCountry,
    selectedJobRefreshPeriod,
    selectedLeadScoreRefreshDataInterval,
    initialJob,
    mappings,
}: {
    jobName: string;
    isApplyFiltersSelected: boolean | null;
    filters: readonly CrmEnrichmentJobFilterType[];
    selectedJobObject: CrmEnrichmentJobObject;
    selectedCountry: ICountryObject;
    selectedJobRefreshPeriod: CrmEnrichmentJobRefreshPeriod;
    selectedLeadScoreRefreshDataInterval: LeadScoreRefreshDataInterval | "";
    initialJob?: CrmEnrichmentJob;
    mappings: readonly EnrichmentJobMappingDTO[];
}) => {
    const accountsColumns = useSelector(selectHsAccountsColumnsRaw);
    const contactColumns = useSelector(selectHsContactsColumnsRaw);
    const columns = selectedJobObject === "company" ? accountsColumns : contactColumns;

    const isNextButtonDisabled = useMemo(() => {
        if (!initialJob || isApplyFiltersSelected === null) {
            return true;
        }
        const {
            name,
            enrichmentObject,
            country,
            refreshDataInterval,
            leadScoreRefreshDataInterval,
            filters: initialFilters,
        } = initialJob;
        const isNoChanges =
            name === jobName &&
            enrichmentObject === selectedJobObject &&
            country === selectedCountry.id &&
            refreshDataInterval === selectedJobRefreshPeriod &&
            (leadScoreRefreshDataInterval ?? "weekly") === (selectedLeadScoreRefreshDataInterval || "weekly") &&
            ((initialFilters.length === 0 && !isApplyFiltersSelected) ||
                crmEnrichmentFiltersService.areFiltersEqualToInitial({ filters, initialFilters, columns })) &&
            areMappingsUnchanged(mappings, initialJob.mappings);

        return (
            isNoChanges ||
            !jobName.trim() ||
            !crmEnrichmentFiltersService.areFiltersValid({ filters, isApplyFiltersSelected }) ||
            !areMappingsValid(mappings)
        );
    }, [
        jobName,
        filters,
        isApplyFiltersSelected,
        initialJob,
        columns,
        selectedJobObject,
        selectedCountry,
        selectedJobRefreshPeriod,
        selectedLeadScoreRefreshDataInterval,
        mappings,
    ]);

    return { isNextButtonDisabled };
};

export default useCrmEnrichmentJobEditorValidation;
